/**
 * 計測モード（地図上に直線・円・多角形を描いて測る）の幾何。
 *
 * 座標はUTM平面の東距・北距 [m]（`{e,n}`）で受け取る。緯度経度のまま
 * 測らないこと — このアプリは距離・方位を一貫してUTM平面・方眼北基準で
 * 出しており（`apps/web/src/goals.ts` の `distanceMeters`/`bearingDeg`）、
 * 競技の読み方もそれに合わせてあるため。
 *
 * ★★ ここで出す数字は**飛行支援のための参考値**であって公式の計測値では
 *   ない。公式の採点・距離は主催者の採点ソフトが行う（airspace.ts・lrn.ts と
 *   同じ方針）。
 *
 * 三角形専用の `lrnAreaKm2`（lrn.ts）は既にテスト済み・出荷済みなので
 * 変更していない。こちらはその一般形（任意の頂点数）。
 */

/** UTM平面上の1点（東距・北距 [m]）。ゾーンは呼び出し側が単一ゾーン内と保証する */
export interface MeasurePoint {
  e: number;
  n: number;
}

/**
 * 多角形の面積 [km²]（シューレース公式）。
 *
 * 頂点は開いた形（最後に始点を繰り返さない）でも閉じた形でも受け付ける。
 * 頂点の並び順（時計回り／反時計回り）に依らず正の値を返す。
 * 2点以下は面積0。自己交差する多角形は符号が打ち消し合うため、
 * 見た目の面積とは一致しない（計測用途では起きにくいので許容する）。
 */
export function polygonAreaKm2(points: readonly MeasurePoint[]): number {
  if (points.length < 3) return 0;
  let twiceArea = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    twiceArea += a.e * b.n - b.e * a.n;
  }
  return Math.abs(twiceArea) / 2 / 1_000_000;
}

/**
 * 折れ線の長さ [m]。`closed`が真なら最後の点から始点へ戻る辺も足す
 * （多角形の周囲長）。1点以下は0。
 */
export function polygonPerimeterM(points: readonly MeasurePoint[], closed = false): number {
  if (points.length < 2) return 0;
  let total = 0;
  for (let i = 0; i < points.length - 1; i++) {
    total += Math.hypot(points[i + 1].e - points[i].e, points[i + 1].n - points[i].n);
  }
  if (closed && points.length > 2) {
    const first = points[0];
    const last = points[points.length - 1];
    total += Math.hypot(first.e - last.e, first.n - last.n);
  }
  return total;
}
